const apiBase = process.env.REACT_APP_API || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("JWtToken") || "";
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

export const contactsService = {
  // Contacts
  getContacts: async (scope, targetUserEmail, search, companyName) => {
    const params = new URLSearchParams();
    if (scope) params.append("scope", scope);
    if (targetUserEmail) params.append("targetUserEmail", targetUserEmail);
    if (search) params.append("search", search);
    if (companyName) params.append("companyName", companyName);

    const url = `${apiBase}/contacts?${params.toString()}`;
    const response = await fetch(url, { method: "GET", headers: getAuthHeaders() });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to fetch contacts");
    }
    const resData = await response.json();
    if (Array.isArray(resData)) return resData;
    if (Array.isArray(resData?.contacts)) return resData.contacts;
    if (Array.isArray(resData?.data)) return resData.data;
    if (Array.isArray(resData?.lstContacts)) return resData.lstContacts;
    return [];
  },

  getContactById: async (id) => {
    const response = await fetch(`${apiBase}/contacts/${id}`, {
      method: "GET",
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error("Failed to fetch contact details");
    }
    return response.json();
  },

  createContact: async (contactData, scope, companyName) => {
    const params = new URLSearchParams();
    const effScope = scope || contactData?.scope;
    const effCompany = companyName || contactData?.companyName;
    if (effScope) params.append("scope", effScope);
    if (effCompany) params.append("companyName", effCompany);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`${apiBase}/contacts${queryString}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(contactData)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to create contact");
    }
    return response.json();
  },

  updateContact: async (id, contactData, scope, companyName) => {
    const params = new URLSearchParams();
    const effScope = scope || contactData?.scope;
    const effCompany = companyName || contactData?.companyName;
    if (effScope) params.append("scope", effScope);
    if (effCompany) params.append("companyName", effCompany);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`${apiBase}/contacts/${id}${queryString}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(contactData)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to update contact");
    }
    return response.json();
  },

  deleteContact: async (id, scope, companyName) => {
    const params = new URLSearchParams();
    if (scope) params.append("scope", scope);
    if (companyName) params.append("companyName", companyName);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`${apiBase}/contacts/${id}${queryString}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to delete contact");
    }
    return response.json();
  },

  importContacts: async (contacts, scope, companyName) => {
    const params = new URLSearchParams();
    if (scope) params.append("scope", scope);
    if (companyName) params.append("companyName", companyName);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`${apiBase}/contacts/import${queryString}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(contacts)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to import contacts");
    }
    return response.json();
  },

  // Contact Groups
  getContactGroups: async (...args) => {
    let [scope, targetUserEmail, search, companyName] = args;
    let effSearch = search;
    let effCompany = companyName;
    if (args.length === 3 && typeof search === "string" && !companyName) {
      effCompany = search;
      effSearch = "";
    }
    const params = new URLSearchParams();
    if (scope) params.append("scope", scope);
    if (targetUserEmail) params.append("targetUserEmail", targetUserEmail);
    if (effSearch) params.append("search", effSearch);
    if (effCompany) params.append("companyName", effCompany);

    const url = `${apiBase}/contact-groups?${params.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message);
    }
    const resData = await response.json();
    if (Array.isArray(resData)) return resData;
    if (Array.isArray(resData?.contactGroups)) return resData.contactGroups;
    if (Array.isArray(resData?.groups)) return resData.groups;
    if (Array.isArray(resData?.data)) return resData.data;
    if (Array.isArray(resData?.lstContactGroups)) return resData.lstContactGroups;
    if (Array.isArray(resData?.lstGroups)) return resData.lstGroups;
    return [];
  },

  getContactGroupById: async (id) => {
    const response = await fetch(`${apiBase}/contact-groups/${id}`, {
      method: "GET",
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error("Failed to fetch contact group details");
    }
    return response.json();
  },

  createContactGroup: async (groupData, scope, companyName) => {
    const params = new URLSearchParams();
    const effScope = scope || groupData?.scope;
    const effCompany = companyName || groupData?.companyName;
    if (effScope) params.append("scope", effScope);
    if (effCompany) params.append("companyName", effCompany);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`${apiBase}/contact-groups${queryString}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(groupData)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message);
    }
    return response.json();
  },

  updateContactGroup: async (id, groupData, scope, companyName) => {
    const params = new URLSearchParams();
    const effScope = scope || groupData?.scope;
    const effCompany = companyName || groupData?.companyName;
    if (effScope) params.append("scope", effScope);
    if (effCompany) params.append("companyName", effCompany);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`${apiBase}/contact-groups/${id}${queryString}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(groupData)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message);
    }
    return response.json();
  },

  deleteContactGroup: async (id, scope, companyName) => {
    const params = new URLSearchParams();
    if (scope) params.append("scope", scope);
    if (companyName) params.append("companyName", companyName);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`${apiBase}/contact-groups/${id}${queryString}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to delete contact group");
    }
    return response.json();
  },

  // Check if contact email already exists under Company scope for logged-in user's company
  checkCompanyContactExists: async (email, companyName) => {
    if (!email) return false;
    const companyContacts = await contactsService.getContacts("Company", "", "", companyName);
    return companyContacts.some((c) => c.email && c.email.toLowerCase() === email.trim().toLowerCase());
  },

  // Country Codes (reused from Docs API)
  getCountryCodes: async () => {
    const response = await fetch(`${apiBase}/docs/GetAllCountryCodedes`, { method: "GET" });
    if (!response.ok) return [];
    return response.json();
  },

  saveContactsFromModal: async ({ selectedContactsToSave, shouldSaveGroups, groupOptions, contactGroups }) => {
    const scope = "Personal";

    let existingContacts = [];
    try {
      existingContacts = await contactsService.getContacts(scope);
    } catch (e) {
      console.warn("Could not fetch contacts in saveContactsFromModal:", e);
    }

    let allGroups = Array.isArray(contactGroups) && contactGroups.length > 0 ? contactGroups : [];
    if (allGroups.length === 0) {
      try {
        allGroups = await contactsService.getContactGroups(scope);
      } catch (e) {
        console.warn("Could not fetch contact groups in saveContactsFromModal:", e);
      }
    }

    for (const item of selectedContactsToSave) {
      const email = (item.email || item.id || "").trim().toLowerCase();
      if (!email) continue;

      const itemDecision = (groupOptions?.itemDecisions || []).find(
        (d) => (d.email || d.contact?.email || d.contact?.id || "").trim().toLowerCase() === email
      );
      const destMode = itemDecision?.destination || (item.isExistingContact ? "group" : "contacts");

      // Check if contact already exists in directory
      let contactObj = (existingContacts || []).find(
        (c) => (c.email || c.Email || "").trim().toLowerCase() === email
      );

      // Create contact if destination mode is "contacts" or "both" (and contact doesn't exist yet)
      if ((destMode === "contacts" || destMode === "both") && !contactObj) {
        const contactPayload = {
          firstName: item.firstName || "",
          lastName: item.lastName || "",
          email: email,
          mobile: item.mobile || "",
          phoneCode: item.phoneCode || "",
          referenceTag: item.referenceTag || "",
          scope: scope
        };
        try {
          contactObj = await contactsService.createContact(contactPayload, scope);
        } catch (e) {
          console.warn("Contact creation warning:", e);
        }
      }

      // If destination mode is "group" and contactObj still not found/created, create contact so it gets a valid DB ID
      if (destMode === "group" && !contactObj) {
        const contactPayload = {
          firstName: item.firstName || "",
          lastName: item.lastName || "",
          email: email,
          mobile: item.mobile || "",
          phoneCode: item.phoneCode || "",
          referenceTag: item.referenceTag || "",
          scope: scope
        };
        try {
          contactObj = await contactsService.createContact(contactPayload, scope);
        } catch (e) {
          console.warn("Contact creation for group mode warning:", e);
        }
      }

      // Group Assignment (if destination mode is "both" or "group")
      if (shouldSaveGroups && groupOptions && (destMode === "both" || destMode === "group")) {
        const targetGroupIds = itemDecision?.groupIds || groupOptions.targetGroupIds || (groupOptions.targetGroupId ? [groupOptions.targetGroupId] : []);

        const contactIdToPush = contactObj?.id || contactObj?._id || contactObj?.Id || item.contactId;

        for (const gId of targetGroupIds) {
          if (!gId) continue;
          const grp = (allGroups || []).find((g) => (g.id || g._id || g.Id) === gId);
          if (grp) {
            const grpId = grp.id || grp._id || grp.Id;
            const currentMemberIds = (grp.memberContactIds || grp.MemberContactIds || grp.members || grp.Members || []).map(
              (m) => (typeof m === "string" ? m : (m.id || m._id || m.Id))
            );
            if (contactIdToPush && !currentMemberIds.includes(contactIdToPush)) {
              const updatedMembers = [...currentMemberIds, contactIdToPush];
              const updatePayload = {
                ...grp,
                id: grpId,
                memberContactIds: updatedMembers,
                MemberContactIds: updatedMembers,
                members: updatedMembers
              };
              await contactsService.updateContactGroup(grpId, updatePayload, scope);
            }
          }
        }
      }
    }
  }
};

export default contactsService;